import { Coords } from 'google-map-react';

/**
 * Returns the radians based on the angle
 */
export const angleToRadians = (angle) => angle * (Math.PI / 180);

/**
 * Given a center and a point on the edge, this function returns the radius in meters
 */
export const getCircleRadius = (center: Coords, edge: Coords) => {
  // D = arccos(sin(lat1) · sin(lat2) + cos(lat1) · cos(lat2) · cos(lon1 - lon2)) * R
  const latCenterRadians = angleToRadians(center.lat);
  const lngCenterRadians = angleToRadians(center.lng);
  const latRadians = angleToRadians(edge.lat);
  const lngRadians = angleToRadians(edge.lng);

  const sin = Math.sin(latRadians) * Math.sin(latCenterRadians);
  const cos = Math.cos(latRadians) * Math.cos(latCenterRadians) * Math.cos(lngRadians - lngCenterRadians);
  const radiusOfEarth = 6371;

  return Math.round(Math.acos(sin + cos) * radiusOfEarth * 1000);
};

// Sort the x / y points
export const sortPointX = (a, b) => a.lng() - b.lng();
export const sortPointY = (a, b) => a.lat() - b.lat();

/////////////////////////////////////////////////////////////////////

// The following two functions are used as is under the copyright.
// Their purpose is to make the hull of the polygon the absolute
// points in order to prevent points being drawn inside the polygon
// and therefore making the geofence hard to draw.

// The functions were slightly modified to follow the ES6 rules

/////////////////////////////////////////////////////////////////////

// Copyright 2001, softSurfer (www.softsurfer.com)
// This code may be freely used and modified for any purpose
// providing that this copyright notice is included with it.
// SoftSurfer makes no warranty for this code, and cannot be held
// liable for any real or imagined damage resulting from its use.
// Users of this code must verify correctness for their application.
const isLeft = (P0, P1, P2) =>
  (P1.lng() - P0.lng()) * (P2.lat() - P0.lat()) - (P2.lng() - P0.lng()) * (P1.lat() - P0.lat());

// Copyright 2001, softSurfer (www.softsurfer.com)
// This code may be freely used and modified for any purpose
// providing that this copyright notice is included with it.
// SoftSurfer makes no warranty for this code, and cannot be held
// liable for any real or imagined damage resulting from its use.
// Users of this code must verify correctness for their application.
/* eslint-disable no-continue */
/* eslint-disable no-param-reassign */
const chainHull2D = (P, n, H) => {
  // the output array H[] will be used as the stack
  let bot = 0;
  let top = -1; // indices for bottom and top of the stack
  let i; // array scan index
  // Get the indices of points with min x-coord and min|max y-coord
  const minmin = 0;
  const xmin = P[0].lng();

  for (i = 1; i < n; i++) {
    if (P[i].lng() !== xmin) {
      break;
    }
  }

  const minmax = i - 1;

  if (minmax === n - 1) {
    // degenerate case: all x-coords == xmin
    H[++top] = P[minmin];

    if (P[minmax].lat() !== P[minmin].lat()) {
      // a nontrivial segment
      H[++top] = P[minmax];
    }

    H[++top] = P[minmin]; // add polygon endpoint

    return top + 1;
  }

  // Get the indices of points with max x-coord and min|max y-coord
  const maxmax = n - 1;
  const xmax = P[n - 1].lng();

  for (i = n - 2; i >= 0; i--) {
    if (P[i].lng() !== xmax) {
      break;
    }
  }

  const maxmin = i + 1;

  // Compute the lower hull on the stack H
  H[++top] = P[minmin]; // push minmin point onto stack
  i = minmax;

  while (++i <= maxmin) {
    // the lower line joins P[minmin] with P[maxmin]
    if (isLeft(P[minmin], P[maxmin], P[i]) >= 0 && i < maxmin) {
      continue; // ignore P[i] above or on the lower line
    }

    while (top > 0) {
      // there are at least 2 points on the stack
      // test if P[i] is left of the line at the stack top
      if (isLeft(H[top - 1], H[top], P[i]) > 0) {
        break; // P[i] is a new hull vertex
      } else {
        top--; // pop top point off stack
      }
    }

    H[++top] = P[i]; // push P[i] onto stack
  }

  // Next, compute the upper hull on the stack H above the bottom hull
  if (maxmax !== maxmin) {
    // if distinct xmax points
    H[++top] = P[maxmax]; // push maxmax point onto stack
  }

  bot = top; // the bottom point of the upper hull stack
  i = maxmin;

  while (--i >= minmax) {
    // the upper line joins P[maxmax] with P[minmax]
    if (isLeft(P[maxmax], P[minmax], P[i]) >= 0 && i > minmax) {
      continue; // ignore P[i] below or on the upper line
    }

    while (top > bot) {
      // at least 2 points on the upper stack
      // test if P[i] is left of the line at the stack top
      if (isLeft(H[top - 1], H[top], P[i]) > 0) {
        break; // P[i] is a new hull vertex
      } else {
        top--; // pop top point off stack
      }
    }

    // breaks algorithm
    if (P[i].lng() === H[0].lng() && P[i].lat() === H[0].lat()) {
      return top + 1; // special case (mgomes)
    }

    H[++top] = P[i]; // push P[i] onto stack
  }

  if (minmax !== minmin) {
    H[++top] = P[minmin]; // push joining endpoint onto stack
  }

  return top + 1;
};

export default chainHull2D;
